import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import { TanuloEllenorzoComponent } from '../admin/tanulo/tanulo-ellenorzo.component';
import { ProfilkepComponent } from '../components/profilkep/profilkep.component';

/**
 *  TANULÓI ADATLAP – egy tanuló minden adata egy helyen, CSAK OLVASÁSRA (backend/tanuloi-adatlap.js).
 *
 *  2026-11-02, a user kérése: „1 helyen a tanulóval kapcsolatos összes dolog látható … a tanulói
 *  lista egy kereshető mező legyen és lazy loadingoljon, hogy ne akarja mind a 400 tanulót
 *  betölteni”. Az adminnak az Adatok menüben, a pedagógusnak a főmenüben (app.component.ts).
 *
 *  KERESŐ: egyetlen mező; gépelésre (300 ms késleltetéssel) a KISZOLGÁLÓ keres név, oktatási
 *  azonosító és osztály szerint, 20-asával; a lista aljára görgetve jön a következő lap. A
 *  pedagógus csak azokat a tanulókat kapja, akiket tanít (vagy akiknek az osztályfőnöke) – ezt a
 *  kiszolgáló dönti el, és az adatlapnál is ugyanígy szűr: ami nem jár neki, az a válaszban sincs.
 *
 *  A TANULÓ ADATAI fülekre bontva. Az „Ellenőrző” fül a szerkesztő adatlap meglévő Ellenőrző-
 *  komponense (órarend, jegyek, számonkérések, mulasztások, késések, feljegyzések, IKSZ,
 *  záradékok, PDF) – csak az első megnyitáskor kerül a lapra, mert tucatnyi kérést indít.
 *  A `?oktazon=` paraméterrel közvetlenül is megnyitható egy tanuló (hivatkozás, könyvjelző).
 */

interface Talalat { oktazon: string; nev: string; osztaly: string; aktiv: boolean; }
type Ful = 'attekintes' | 'ellenorzo' | 'csalad' | 'tamogatas' | 'egyeb';

/** A törzsadat-mezők sorrendje és felirata; ami a válaszban nincs (a szerep miatt), nem jelenik meg. */
const TORZS_MEZOK: { kulcs: string; cimke: string }[] = [
  { kulcs: 'oktazon', cimke: 'Oktatási azonosító' },
  { kulcs: 'szul_datum', cimke: 'Születési idő' },
  { kulcs: 'nem', cimke: 'Nem' },
  { kulcs: 'email', cimke: 'Iskolai e-mail (belépés)' },
  { kulcs: 'szuletesi_nev', cimke: 'Születési név' },
  { kulcs: 'szul_hely', cimke: 'Születési hely' },
  { kulcs: 'anyja_neve', cimke: 'Anyja neve' },
  { kulcs: 'allampolgarsag', cimke: 'Állampolgárság' },
  { kulcs: 'telefon', cimke: 'Telefon' },
  { kulcs: 'allando_cim', cimke: 'Állandó lakcím' },
  { kulcs: 'tartozkodasi_cim', cimke: 'Tartózkodási cím' },
  { kulcs: 'tankot_vege', cimke: 'Tankötelezettség vége' },
  { kulcs: 'taj', cimke: 'TAJ' },
  { kulcs: 'adoazon', cimke: 'Adóazonosító jel' },
  { kulcs: 'igazolvany', cimke: 'Igazolvány' },
  { kulcs: 'diakigazolvany_kelte', cimke: 'Diákigazolvány kelte' },
  { kulcs: 'beirasi_naplo_sorszam', cimke: 'Beírási napló sorszáma' },
  { kulcs: 'meresi_azonosito', cimke: 'Mérési azonosító' },
];

const DOK_TIPUS: Record<string, string> = {
  bizonyitvany: 'Bizonyítvány', torzslap: 'Törzslap', zaradek: 'Záradék', igazolas: 'Igazolás',
};

@Component({
  selector: 'app-tanuloi-adatlap',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, TanuloEllenorzoComponent, ProfilkepComponent],
  templateUrl: './tanuloi-adatlap.component.html',
  styleUrls: ['./tanuloi-adatlap.component.scss'],
})
export class TanuloiAdatlapComponent implements OnInit {
  readonly LAP = 20;
  readonly TORZS_MEZOK = TORZS_MEZOK;

  intezmeny = '';
  ev: number | null = null;

  /* ── Kereső ── */
  kereso = '';
  talalatok: Talalat[] = [];
  tovabb = false;
  keresFut = false;
  keresHiba = '';
  nyitva = false;
  kijelolt = -1;
  /** Az admin a korábbi tanévek tanulóit is keresheti (a kiszolgáló csak adminnak veszi figyelembe). */
  regi = false;
  admin = false;
  private keresJel = 0;
  @ViewChild('keresoDoboz') keresoDoboz?: ElementRef<HTMLElement>;

  /* ── A kiválasztott tanuló ── */
  tanulo: Talalat | null = null;
  adat: any = null;
  adatFut = false;
  adatHiba = '';
  ful: Ful = 'attekintes';
  /** Az Ellenőrző fület megnyitották-e már (addig nem kerül a lapra, nem indít kérést). */
  ellenorzoNyitva = false;
  private adatJel = 0;
  readonly ma = new Date().toLocaleDateString('sv-SE');

  constructor(
    private http: HttpClient,
    private data: DataService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    const sajat: any = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
    this.intezmeny = String(sajat?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    this.ev = await this.data.getAktualisTanev().catch(() => null);
    const okt = String(this.route.snapshot.queryParamMap.get('oktazon') || '').trim();
    if (/^\d{6,20}$/.test(okt)) this.valaszt({ oktazon: okt, nev: '', osztaly: '', aktiv: true }, false);
  }

  /* ── Kereső ─────────────────────────────────────────────────────────────── */

  private evParam(): string { return this.ev ? `&ev=${this.ev}` : ''; }

  /** A kereső mező változott (a searchbar maga késleltet): elölről keres. */
  keresValt(ertek: any): void {
    this.kereso = String(ertek ?? '');
    this.nyitva = true;
    this.keres(true);
  }

  fokusz(): void {
    this.nyitva = true;
    if (!this.talalatok.length && !this.keresFut) this.keres(true);
  }

  regiValt(): void { this.keres(true); }

  /**
   *  Egy lap a kiszolgálótól. `elolrol`: új keresés (az eddigi lista eldobva), különben a
   *  következő lap a lista végére. A `keresJel` miatt egy későn érkező régi válasz nem írhatja
   *  felül az újat (gyors gépelés).
   */
  async keres(elolrol: boolean): Promise<void> {
    if (!this.intezmeny) { this.keresHiba = 'Nincs kiválasztott intézmény.'; return; }
    if (!elolrol && (this.keresFut || !this.tovabb)) return;
    const jel = ++this.keresJel;
    const tol = elolrol ? 0 : this.talalatok.length;
    this.keresFut = true;
    this.keresHiba = '';
    if (elolrol) { this.kijelolt = -1; }
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/tanuloi-adatlap/kereses/${encodeURI(this.intezmeny)}?q=${encodeURIComponent(this.kereso.trim())}`
        + `&tol=${tol}&db=${this.LAP}${this.evParam()}${this.regi ? '&regi=1' : ''}`));
      if (jel !== this.keresJel) return;
      const uj: Talalat[] = Array.isArray(r?.tanulok) ? r.tanulok : [];
      this.talalatok = elolrol ? uj : [...this.talalatok, ...uj];
      this.tovabb = !!r?.tovabb;
      this.admin = !!r?.admin;
    } catch (e: any) {
      if (jel !== this.keresJel) return;
      if (elolrol) this.talalatok = [];
      this.tovabb = false;
      this.keresHiba = e?.error?.error || 'A keresés most nem sikerült.';
    } finally {
      if (jel === this.keresJel) this.keresFut = false;
    }
  }

  /** LUSTA BETÖLTÉS: a lista aljához közeledve jön a következő lap. */
  listaGorget(ev: Event): void {
    const el = ev.target as HTMLElement;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 60) this.keres(false);
  }

  billentyu(ev: KeyboardEvent): void {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault(); this.nyitva = true;
      this.kijelolt = Math.min(this.kijelolt + 1, this.talalatok.length - 1);
      if (this.kijelolt >= this.talalatok.length - 3) this.keres(false);
      this.kijeloltLatszik();
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault(); this.kijelolt = Math.max(this.kijelolt - 1, 0); this.kijeloltLatszik();
    } else if (ev.key === 'Enter') {
      const t = this.talalatok[this.kijelolt >= 0 ? this.kijelolt : 0];
      if (t && this.nyitva) { ev.preventDefault(); this.valaszt(t); }
    } else if (ev.key === 'Escape') {
      this.nyitva = false;
    }
  }

  private kijeloltLatszik(): void {
    setTimeout(() => {
      const el = this.keresoDoboz?.nativeElement.querySelector('.ta-talalat.kijelolt') as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  /** A legördülő a mezőn kívüli kattintásra bezárul. */
  @HostListener('document:click', ['$event'])
  kivulKattint(ev: MouseEvent): void {
    if (!this.nyitva) return;
    const doboz = this.keresoDoboz?.nativeElement;
    if (doboz && !doboz.contains(ev.target as Node)) this.nyitva = false;
  }

  /* ── A kiválasztott tanuló ──────────────────────────────────────────────── */

  valaszt(t: Talalat, urlba = true): void {
    this.tanulo = t;
    this.nyitva = false;
    if (urlba) {
      this.router.navigate([], { relativeTo: this.route, queryParams: { oktazon: t.oktazon }, replaceUrl: true });
    }
    this.betolt();
  }

  async betolt(): Promise<void> {
    if (!this.tanulo || !this.intezmeny) return;
    const jel = ++this.adatJel;
    this.adatFut = true;
    this.adatHiba = '';
    this.adat = null;
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/tanuloi-adatlap/tanulo/${encodeURIComponent(this.tanulo.oktazon)}/${encodeURI(this.intezmeny)}${this.ev ? `?ev=${this.ev}` : ''}`));
      if (jel !== this.adatJel) return;
      this.adat = r;
      //  A szerep miatt nem látható fülről (Egyéb) az Áttekintésre.
      if (this.ful === 'egyeb' && !this.egyebLathato) this.ful = 'attekintes';
    } catch (e: any) {
      if (jel !== this.adatJel) return;
      this.adatHiba = e?.error?.error || 'Az adatlap most nem tölthető be.';
    } finally {
      if (jel === this.adatJel) this.adatFut = false;
    }
  }

  fulValt(): void {
    if (this.ful === 'ellenorzo') this.ellenorzoNyitva = true;
  }

  /* ── Megjelenítés ───────────────────────────────────────────────────────── */

  get t(): any { return this.adat?.torzsadat || {}; }

  get szerepSzoveg(): string {
    const sz = this.adat?.jog?.szerep;
    return sz === 'admin' ? 'Adminisztrátorként látod' : sz === 'osztalyfonok' ? 'Osztályfőnökként látod' : 'Tanárként látod';
  }

  /** A mostani (nyitott) jogviszony, ha nincs, a legutóbbi. */
  get aktivJogviszony(): any {
    const lista: any[] = this.adat?.jogviszony || [];
    return lista.find((j) => !j.vege || j.vege >= this.ma) || lista[0] || null;
  }

  get egyeniMunkarendek(): any[] {
    return (this.adat?.jogviszony || []).filter((j: any) => j.egyeni_tanrend);
  }

  get egyebLathato(): boolean {
    return !!this.adat && (this.adat.etkezes != null || this.adat.fegyelmi != null || this.adat.dokumentumok != null);
  }

  torzsErtek(kulcs: string): string {
    const v = this.t[kulcs];
    return v === null || v === undefined || v === '' ? '–' : String(v);
  }
  vanMezo(kulcs: string): boolean { return Object.prototype.hasOwnProperty.call(this.t, kulcs); }

  statuszNev(s: any): string {
    const n = Number(s);
    return n === 1 ? 'aktív' : n === 2 ? 'szünetel' : (s == null ? '–' : String(s));
  }

  felmentesMiAlol(f: any): string {
    const r: string[] = [];
    if (+f?.oralatogatas_aloli) r.push('óralátogatás');
    if (+f?.ertekeles_aloli) r.push('értékelés');
    if (+f?.szovegesen_ertekelheto) r.push('szövegesen értékelhető');
    return r.length ? r.join(', ') : 'kedvezmény';
  }
  felmentesEl(f: any): boolean { return !f?.vege || String(f.vege).startsWith('0000') || f.vege >= this.ma; }

  /** A mulasztás tantárgyankénti szintje → téma-szín (mulasztas-arany.js › aranySzint). */
  szintSzin(szint: string): string {
    return ({ piros: 'danger', narancs: 'warning', sarga: 'warning', rendben: 'success' } as Record<string, string>)[szint] || 'medium';
  }
  szintNev(szint: string): string {
    return ({ piros: '30% fölött', narancs: '25% fölött', sarga: '20% fölött', rendben: 'rendben', keves: 'kevés óra még', nincs: '–' } as Record<string, string>)[szint] || szint;
  }

  get minosit(): any { return this.adat?.minosithetoseg || null; }
  get minositSzoveg(): { szin: string; szoveg: string } {
    const m = this.minosit;
    if (!m || m.hiba) return { szin: 'medium', szoveg: m?.hiba || 'Nincs adat.' };
    if (m.nem_minositheto) return { szin: 'danger', szoveg: 'A mulasztás alapján a tanév végén NEM MINŐSÍTHETŐ (250 óra vagy egy tantárgyból 30% fölött) – osztályozó vizsga a nevelőtestület engedélyével.' };
    if (m.osszes_figyel) return { szin: 'warning', szoveg: 'Figyelem: az összes mulasztás 200 óra fölött van (a határ 250).' };
    return { szin: 'success', szoveg: 'A mulasztás alapján minősíthető.' };
  }

  dokTipus(t: string): string { return DOK_TIPUS[t] || t || '–'; }
}
